/**
 * Address autocomplete.
 *
 * Uses Google's Places library, loaded from their CDN when a street field is
 * first focused — not on every page. Off entirely unless
 * VITE_GOOGLE_MAPS_KEY is set, so the app works exactly as before without it.
 *
 * Session tokens matter here and are easy to get wrong. Keystroke requests
 * inside a session are free; the session ends with one details call, which is
 * the only billed part. Without a token every keystroke bills separately, so
 * a five-word street address costs many times what it should.
 */

const KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;

export const addressLookupConfigured = () => Boolean(KEY);

export interface AddressParts {
  street: string;
  city: string;
  state: string;
  zip: string;
  county: string;
}

interface PlacesNS {
  AutocompleteSessionToken: new () => object;
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions(req: Record<string, unknown>): Promise<{
      suggestions: {
        placePrediction: {
          text: { text: string };
          toPlace(): {
            fetchFields(o: { fields: string[] }): Promise<void>;
            addressComponents?: {
              types: string[]; longText: string; shortText: string;
            }[];
          };
        } | null;
      }[];
    }>;
  };
}

/** Poll briefly for the maps namespace, rather than testing once and giving
    up. Cheap, and it removes a whole class of timing failure. */
function waitForMaps(ms: number): Promise<{ importLibrary?: (n: string) => Promise<unknown> } | null> {
  const started = Date.now();
  return new Promise((resolve) => {
    const look = () => {
      const g = (window as unknown as {
        google?: { maps?: { importLibrary?: (n: string) => Promise<unknown> } };
      }).google;
      if (g?.maps?.importLibrary) return resolve(g.maps);
      if (Date.now() - started > ms) return resolve(g?.maps ?? null);
      setTimeout(look, 60);
    };
    look();
  });
}

let loader: Promise<PlacesNS> | null = null;

/**
 * Google reports a rejected key by calling this global, not by failing the
 * request. Without it the only sign is a line in the console, and the field
 * waits forever for suggestions that are never coming.
 */
declare global {
  interface Window { gm_authFailure?: () => void }
}
let authFailed = false;

function loadPlaces(): Promise<PlacesNS> {
  if (!KEY) return Promise.reject(new Error("No Google Maps key configured."));
  if (loader) return loader;

  loader = new Promise<PlacesNS>((resolve, reject) => {
    /* Nothing here may hang. Every path below either resolves or rejects, and
       a timeout catches anything that manages to do neither — a field stuck
       on "Looking" tells a person nothing and cannot be recovered from. */
    const timer = setTimeout(() => {
      loader = null;
      reject(new Error("Google did not respond. Type the address by hand."));
    }, 10_000);

    const settle = {
      ok: (v: PlacesNS) => { clearTimeout(timer); resolve(v); },
      no: (e: Error) => { clearTimeout(timer); loader = null; reject(e); },
    };

    window.gm_authFailure = () => {
      authFailed = true;
      settle.no(new Error(
        "Google rejected the key. Check that Places API (New) is enabled and "
        + "that this site is allowed under the key's website restrictions."));
    };

    const ready = async () => {
      try {
        /* Even after the callback, give importLibrary a moment to appear:
           different versions of the loader attach it at slightly different
           points, and a hard failure here is a dead field for the person
           typing. */
        const g = await waitForMaps(3000);
        if (!g?.importLibrary) {
          throw new Error(
            "Google Maps loaded but the places library did not. Type the "
            + "address by hand.");
        }
        const lib = await g.importLibrary("places");
        settle.ok(lib as unknown as PlacesNS);
      } catch (e) {
        // Previously this threw inside the executor and was swallowed, so the
        // promise never settled and the field waited forever.
        settle.no(e instanceof Error ? e : new Error("Could not load Google Places."));
      }
    };

    const w = window as unknown as {
      google?: { maps?: { importLibrary?: (n: string) => Promise<unknown> } };
    };
    if (w.google?.maps?.importLibrary) { void ready(); return; }

    /* Google calls this when the API is actually usable.
       The script's own onload only means the file arrived — with
       loading=async the API bootstraps afterwards, so calling importLibrary
       at onload sometimes found nothing there and reported the places
       library as missing. A race, which is why it worked some days and not
       others. The callback removes the guesswork. */
    const ping = `__frontlotMapsReady_${Date.now()}`;
    (window as unknown as Record<string, unknown>)[ping] = () => {
      delete (window as unknown as Record<string, unknown>)[ping];
      void ready();
    };

    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(KEY)}`
          + `&libraries=places&loading=async&v=weekly&callback=${ping}`;
    s.async = true;
    s.onerror = () => settle.no(new Error("Could not reach Google. Type the address by hand."));
    document.head.appendChild(s);
  });
  return loader;
}

/* A session is one address being typed. Held here so every keystroke joins
   the same session and only the final details call is billed. */
let session: object | null = null;

export interface Suggestion {
  label: string;
  /** Resolves the full address. Ends the billing session. */
  resolve(): Promise<AddressParts>;
}

/** The reason the last lookup returned nothing, when there was one. Shown in
    the field rather than swallowed: a silent empty list is indistinguishable
    from "no such address", and that cost an evening. */
export let lastLookupError: string | null = null;

/** Suggestions for what has been typed so far. Never throws — a lookup that
    cannot run must not block someone typing an address by hand. */
export async function suggestAddresses(input: string): Promise<Suggestion[]> {
  lastLookupError = null;
  if (!KEY || input.trim().length < 4) return [];
  try {
    const places = await loadPlaces();
    if (!session) session = new places.AutocompleteSessionToken();

    const { suggestions } = await places.AutocompleteSuggestion
      .fetchAutocompleteSuggestions({
        input,
        sessionToken: session,
        includedRegionCodes: ["us"],
        /* No includedPrimaryTypes filter. Restricting to street_address and
           premise looked tidy and returned nothing for a part-typed address —
           Google needs to match a broader set while the string is still
           incomplete, and the address components are filtered afterwards
           anyway. */
      });

    return suggestions
      .map((s) => s.placePrediction)
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
      .map((p) => ({
        label: p.text.text,
        resolve: async () => {
          const place = p.toPlace();
          // Only address components: this is the cheapest field set, and
          // asking for anything else silently moves the call up a price tier.
          await place.fetchFields({ fields: ["addressComponents"] });
          session = null;               // the session ends with this call
          return partsFrom(place.addressComponents ?? []);
        },
      }));
  } catch (e) {
    lastLookupError = authFailed
      ? "Google rejected the key — check Places API (New) is enabled and this "
        + "site is allowed under the key restrictions."
      : e instanceof Error ? e.message : "Address lookup failed.";
    // Also to the console: the message above is for the person at the desk,
    // this one is for whoever is working out why.
    console.error("Address lookup failed:", e);
    return [];
  }
}

function partsFrom(components: { types: string[]; longText: string; shortText: string }[]): AddressParts {
  const get = (type: string, short = false) => {
    const c = components.find((x) => x.types.includes(type));
    return c ? (short ? c.shortText : c.longText) : "";
  };
  const number = get("street_number");
  const road = get("route");
  return {
    street: [number, road].filter(Boolean).join(" "),
    city: get("locality") || get("sublocality") || get("postal_town"),
    state: get("administrative_area_level_1", true),
    zip: get("postal_code"),
    // Google returns "Lee County"; the counties table stores "Lee".
    county: get("administrative_area_level_2").replace(/\s+County$/i, ""),
  };
}
